import {Component, OnInit, ViewEncapsulation} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {TranslocoService} from '@jsverse/transloco';
import {ActivePromotionConfig} from '../config/active-promotion.config';

/**
 * 神戸オークション 新規ユーザ登録 (公開ページ, issue #255)。ユーザ管理に表示する QR コードから開く。
 * ログイン不要。プライバシーポリシーに同意したら送信でき、申請はユーザ管理に PENDING で載る。
 * 管理者が顧客を紐付けて承認すると、パスワード設定のメールが届く。
 */
@Component({
    standalone: false,
    selector: 'kobeac-register',
    templateUrl: 'register.component.html',
    styleUrls: ['register.component.css'],
    encapsulation: ViewEncapsulation.None,
})
export class KobeacRegisterComponent implements OnInit {
    /** 公開 API (認証不要) */
    static readonly API = '/rest/public/kobeac/registration';

    form = {
        companyName: '',
        tradeName: '',
        antiqueLicenseNo: '',
        invoiceNo: '',
        representative: '',
        email: '',
    };
    policy = '';
    policyLoaded = false;
    policyAccepted = false;
    sending = false;
    sent = false;
    /** サーバが返した入力エラーなど。画面にそのまま出す */
    error = '';

    constructor(private http: HttpClient, private transloco: TranslocoService) {
    }

    ngOnInit(): void {
        this.http.get(ActivePromotionConfig.SERVER_URL + KobeacRegisterComponent.API + '/policy').subscribe({
            next: (res: any) => {
                this.policy = res && res.policy ? res.policy : '';
                this.policyLoaded = true;
            },
            error: () => {
                this.policyLoaded = true;
                this.error = this.transloco.translate('register.policy_load_failed');
            },
        });
    }

    private trimmed(v: string): string {
        return (v || '').trim();
    }

    get emailValid(): boolean {
        return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(this.trimmed(this.form.email));
    }

    /** インボイス登録番号は任意。入れたら T+13 桁 (T・ハイフン・空白は省略可) */
    get invoiceValid(): boolean {
        const v = this.invoiceDigits;
        return !v || /^[Tt]?\d{13}$/.test(v);
    }

    /** 全角・ハイフン・長音記号・空白を除いた番号 */
    private get invoiceDigits(): string {
        return this.trimmed(this.form.invoiceNo).replace(/[\s\-ー－‐]/g, '')
            .replace(/[０-９Ｔｔ]/g, (c) => String.fromCharCode(c.charCodeAt(0) - 0xFEE0));
    }

    get nameValid(): boolean {
        return !!(this.trimmed(this.form.companyName) || this.trimmed(this.form.tradeName));
    }

    get canSubmit(): boolean {
        return !this.sending && this.policyLoaded && !!this.policy && this.policyAccepted && this.nameValid
            && !!this.trimmed(this.form.representative) && this.emailValid && this.invoiceValid;
    }

    submit(): void {
        if (!this.canSubmit) {
            return;
        }
        this.sending = true;
        this.error = '';
        const body = Object.assign({}, this.form, {invoiceNo: this.invoiceDigits, policyAccepted: true});
        this.http.post(ActivePromotionConfig.SERVER_URL + KobeacRegisterComponent.API, body).subscribe({
            next: () => {
                this.sending = false;
                this.sent = true;
            },
            error: (e: any) => {
                this.sending = false;
                this.error = (e && e.error && e.error.message) ? e.error.message : this.transloco.translate('register.send_failed');
            },
        });
    }
}
